// Game events (spec/dashboard.md section 7.2): the card list over SmartObjects.GameEvent and its heirs, the time status, the
// chips, the inline attachment, and the wizards: New offer (OfferWizard: an offer with the event that shows it) and the
// group offer event, which stages an event with a group attached in one go.
import { useState } from 'react';
import type { Json } from '../../api/types';
import { CardList } from '../../content/cards/CardList';
import { CONDITION_NS } from '../../content/conditions/model';
import { useContent } from '../../content/ContentContext';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { AlertCircle, AlertTriangle, CalendarDays } from '../../ui/icons';
import { Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput, DurationInput } from '../../ui/values';
import { EVENT, EventBody, EventHeader, GROUP, STORE_ITEM, stage } from './cards';
import { OfferWizard } from './OfferWizard';
import './liveops.css';

export function EventsPage() {
  const [wizard, setWizard] = useState<null | 'offer' | 'group'>(null);
  return (
    <CardList
      id="events"
      title="Game events"
      template={EVENT}
      calendarColor
      header={(ctx) => <EventHeader ctx={ctx} />}
      body={(ctx) => <EventBody ctx={ctx} />}
      createExtras={[
        { key: 'offer', label: 'New offer…', run: () => setWizard('offer') },
        { key: 'group', label: 'Group offer event…', run: () => setWizard('group') },
      ]}
      filterFields={[
        { key: 'data.finishType', label: 'Finish type', type: 'enum', group: 'Event', options: [{ value: '0', label: 'Condition' }, { value: '1', label: 'Duration' }, { value: '2', label: 'From script' }] },
        { key: 'data.unnyPriority', label: 'Priority', type: 'number', group: 'Event' },
        { key: 'data.limit', label: 'Limit', type: 'number', group: 'Event' },
        { key: 'data.manualRemove', label: 'Manual remove', type: 'bool', group: 'Event' },
      ]}
      emptyText="No game events yet. Create one, or start from New offer…"
      emptyIcon={<CalendarDays aria-hidden="true" />}
    >
      {wizard === 'offer' && <OfferWizard onClose={() => setWizard(null)} openEvent />}
      {wizard === 'group' && <GroupEventWizard onClose={() => setWizard(null)} />}
    </CardList>
  );
}

/** Group offer event: one event with a Dates range and an offer group attached, all staged together (T37). */
export function GroupEventWizard({ onClose }: { onClose: () => void }) {
  const { workspace, host } = useWidgetEnv();
  const catalog = useContent();
  const { offsets } = useGame();
  const toasts = useToasts();
  const now = Math.floor(Date.now() / 1000);
  const [name, setName] = useState('');
  const [start, setStart] = useState<number>(now - (now % 3600) + 3600);
  const [finish, setFinish] = useState<number>(now - (now % 3600) + 3600 + 7 * 86400);
  const [repeat, setRepeat] = useState(0);
  const [storeItems, setStoreItems] = useState<Json>([]);
  const [duration, setDuration] = useState(0);
  const [groupType, setGroupType] = useState(1);
  const storeParams = catalog.templates.get(STORE_ITEM);
  const canCreate = name.trim().length > 0 && finish > start && Array.isArray(storeItems) && storeItems.length > 0;
  const create = () => {
    try {
      const key = name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '') || 'EVENT';
      const stringId = workspace.create('string', { id: '', group: 'LIVEOPS', key: `${key}_NAME`, description: null, export: true, values: { [catalog.defaultLanguage]: name.trim() } } as unknown as Json, { label: `LIVEOPS/${key}_NAME` });
      const dates = stage(workspace, catalog, `${CONDITION_NS}TimeRage`, { start, finish, repeat });
      const root = stage(workspace, catalog, `${CONDITION_NS}And`, { conditions: [dates] });
      const attachment = stage(workspace, catalog, GROUP, { name: stringId, storeItems, duration, type: groupType, wait: false });
      const event = stage(workspace, catalog, EVENT, { name: stringId, condition: root, script: attachment, finishType: 0 });
      toasts.success('Event and offer group staged; Save All commits them together.');
      onClose();
      void host?.open(event);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Modal
      title="Group offer event"
      open
      onClose={onClose}
      size="l"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} disabled={!canCreate}>
            Create (staged)
          </Button>
        </>
      }
    >
      <div className="lo-wizard">
        <div className="form-grid">
          <Field label="Name" required help={`A localization string LIVEOPS/<NAME>_NAME is staged with it (${catalog.defaultLanguage})`} className="span-2">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus />
          </Field>
        </div>
        <div className="section-head">
          <h3>When it runs</h3>
        </div>
        <div className="form-grid">
          <Field label="Starts">
            <DateTimeInput value={start} onCommit={(s) => s !== null && setStart(s)} ctx={{ offsets }} aria-label="Starts" />
          </Field>
          <Field label="Ends">
            <DateTimeInput value={finish} onCommit={(s) => s !== null && setFinish(s)} ctx={{ offsets }} aria-label="Ends" />
          </Field>
          <Field label="Repeat every" help="0 = never">
            <DurationInput value={repeat} onCommit={setRepeat} zeroMeans="never" aria-label="Repeat every" />
          </Field>
        </div>
        {finish <= start && (
          <p className="lo-note danger" role="alert">
            <AlertCircle aria-hidden="true" /> The range is empty.
          </p>
        )}
        {repeat > 0 && repeat <= finish - start && (
          <p className="lo-note warning">
            <AlertTriangle aria-hidden="true" /> The repeat must be longer than the range.
          </p>
        )}
        <div className="section-head">
          <h3>The offer group</h3>
        </div>
        <div className="form-grid">
          <Field label="Group type">
            <Select value={String(groupType)} onChange={(e) => setGroupType(Number(e.target.value))} aria-label="Group type">
              <option value="0">Unlimited purchases</option>
              <option value="1">Chain deals</option>
              <option value="2">Only one purchase</option>
              <option value="3">Purchase each offer once</option>
            </Select>
          </Field>
          <Field label="Group duration" help="0 = ends with the event">
            <DurationInput value={duration} onCommit={setDuration} zeroMeans="ends with the event" aria-label="Group duration" />
          </Field>
          <Field label="Store items" required help="The order is the chain order" className="span-2">
            {storeParams ? <FieldWidget param={{ ...storeParams.effectiveParameters[0]!, name: 'storeItems', displayName: 'Store items', type: 'list', template: null, of: { ...storeParams.effectiveParameters[0]!, name: 'of', type: 'document', template: STORE_ITEM, of: null } }} value={storeItems} onCommit={setStoreItems} /> : null}
          </Field>
        </div>
      </div>
    </Modal>
  );
}
